import BlueButtonOutlined from "@/components/styled/blue.button.outlined";
import GreenButtonContained from "@/components/styled/green.button.contained";
import SwitchComponent from "@/components/styled/switch.component";
import popupService from "@/services/popupService";
import userStore from "@/stores/user.store";
import { Box, FormControl, FormControlLabel, Grid, Toolbar, Typography } from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    unstable_Blocker as Blocker,
    unstable_BlockerFunction as BlockerFunction,
    useNavigate,
    useParams,
    useBlocker,
} from "react-router-dom";
import "./users.page.css";
import personalIcon from "@/assets/icons/personal-info.svg";
import contactInfoIcon from "@/assets/icons/contact-info.svg";
import locationIcon from "@/assets/icons/location.svg";
import CreateEntityBox from "@/components/styled/create.entity.box";
import UserProfileSelect from "@/components/user/user.profile.select";
import InputFilterFieldStyled from "@/components/styled/input.filter.field.styled";
import { CreateUserRequest, User } from "@/models/user";
import LoginInputFormStyled from "@/components/styled/login.input.form.styled";
import { IconButton, InputAdornment, InputLabel, OutlinedInput } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import swal from "sweetalert2";
import PasswordStrengthMeter from "@/components/common/passwordStrengthMeter/passwordStrengthMeter";

const SettingsUserDetailsPage: FC = () => {
    const { id } = useParams();
    const { userId } = useParams();
    const [user, setUserData] = useState<CreateUserRequest>(new CreateUserRequest());
    const [originalUser, setOriginalUser] = useState<CreateUserRequest>(new CreateUserRequest());
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isSave, setIsSave] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleMobilePhoneChange = (event: any) => {
        const inputValue = event.target.value;
        const isValid = /^[0-9+]+$/.test(inputValue);

        if (inputValue === "") {
            setUserData(() => ({
                ...user,
                cellPhoneNumber: "",
            }));
        }
        if (isValid) {
            setUserData(() => ({
                ...user,
                cellPhoneNumber: event.target.value.substring(0, 15),
            }));
        }
    };

    const handleWorkPhoneChange = (event: any) => {
        const inputValue = event.target.value;
        const isValid = /^[0-9+]+$/.test(inputValue);

        if (inputValue === "") {
            setUserData(() => ({
                ...user,
                phoneNumber: "",
            }));
        }
        if (isValid) {
            setUserData(() => ({
                ...user,
                phoneNumber: event.target.value.substring(0, 15),
            }));
        }
    };

    const handleCancel = () => {
        navigate(`/settings/manage-users`);
    };

    const submitChanges = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (userId && parseInt(userId) <= 0) {
            if (!user.userPassword) {
                popupService.showSuccess(t("login.PasswordEmpty"));
            }
            swal.showLoading(null);
            var createUserResponse = await userStore.createUser(user);
            if (createUserResponse?.data) {
                swal.close();
                popupService.showSuccess(t("user.CreateSuccess")).then(() => {
                    setIsSave(true);
                    handleCancel();
                });
                resetUser();
            }
            return;
        }
        if (userId && parseInt(userId) > 0) {
            const userRequest = new User();
            userRequest.idCard = user.idCard;
            userRequest.firstName = user.firstName;
            userRequest.lastName = user.lastName;
            userRequest.username = user.username;
            userRequest.userPassword = user.userPassword;
            userRequest.email = user.email;
            userRequest.phoneNumber = user.phoneNumber;
            userRequest.cellPhoneNumber = user.cellPhoneNumber;
            userRequest.userId = parseInt(userId);
            userRequest.profileId = user.profileId;
            userRequest.isActive = user.isActive;
            if (id) {
                userRequest.organizationId = Number(id);
            }
            swal.showLoading(null);
            var editUserResponse = await userStore.editUser(userRequest);
            if (editUserResponse?.data) {
                swal.close();
                await popupService.showSuccess(t("user.EditSuccess")).then(() => {
                    setIsSave(true);
                });
                handleCancel();
            }
        }
    };
    const resetUser = () => {
        setUserData(new CreateUserRequest());
    };
    async function startFetching() {
        if (userId && parseInt(userId) <= 0) {
            return;
        }
        swal.showLoading(null);
        var response = await userStore.getUserInfo(Number(userId));
        if (response) {
            swal.close();
            setEditMode(true);
            setUserData(response.data);
            setOriginalUser(response.data);
            userStore.setUserData(response.data);
        }
    }

    const shouldBlock: BlockerFunction = React.useCallback(
        ({ currentLocation, nextLocation }) => {
            const userDataString = JSON.stringify(user);
            const userOriginDataString = JSON.stringify(originalUser);

            return (
                editMode &&
                !isSave &&
                userDataString !== userOriginDataString &&
                currentLocation.pathname !== nextLocation.pathname
            );
        },
        [user, originalUser, isSave, editMode]
    );

    const blocker = useBlocker(shouldBlock);

    const handleShowDiscardMsg = (blocker: Blocker) => {
        if (blocker.state === "blocked") {
            popupService.wantDiscard(t("general.ChangesMadeNotSavedMsg")).then((result) => {
                if (result.isConfirmed) {
                    blocker?.proceed?.();
                    return true;
                } else {
                    blocker?.reset?.();
                    return false;
                }
            });
        }
        return false;
    };

    const handleGenerateId = (): string => {
        const array = new Uint32Array(4);
        crypto.getRandomValues(array);
        return array.join("-");
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, []);

    return (
        <div className="settings-user">
            {blocker ? handleShowDiscardMsg(blocker) : null}
            <Box
                sx={{
                    background: "#FFFFFF",
                    border: "1px solid rgba(64, 82, 137, 0.25)",
                    boxShadow: "4px 4px 20px rgb(64 82 137 / 25%)",
                    borderRadius: "12px",
                }}
            >
                <Toolbar
                    sx={{
                        backgroundColor: "#4373A1",
                        borderTopRightRadius: "12px",
                        borderTopLeftRadius: "12px",
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: "600",
                            fontSize: "20px",
                            lineHeight: "23px",
                            color: "#fff",
                        }}
                    >
                        {user && user.firstName ? user.firstName + " " + user.lastName : t("user.NewUser")}
                    </Typography>
                </Toolbar>
                <Grid
                    className="create-user-grid"
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="flex-start"
                    component="form"
                    autoComplete="off"
                    onSubmit={submitChanges}
                    sx={{
                        padding: "0 0 30px",
                    }}
                >
                    <Grid md={4} item className="create-user-column">
                        <Toolbar>
                            <Box
                                component="img"
                                sx={{
                                    height: "20.36px",
                                    width: "22.75px",
                                }}
                                alt={t("user.PersonalInfo")}
                                src={personalIcon}
                            />
                            <Box className="create-user-title">{t("user.PersonalInfo")}</Box>
                        </Toolbar>
                        <CreateEntityBox>
                            <FormControl variant="standard">
                                <InputFilterFieldStyled
                                    label={t("user.IdCard")}
                                    size="small"
                                    autoComplete="new-password"
                                    onChange={(event) =>
                                        setUserData(() => ({
                                            ...user,
                                            idCard: event.target.value,
                                        }))
                                    }
                                    value={user.idCard}
                                    inputProps={{
                                        inputMode: "numeric",
                                        maxLength: 9,
                                    }}
                                    required
                                ></InputFilterFieldStyled>
                            </FormControl>
                            <FormControl variant="standard">
                                <InputFilterFieldStyled
                                    label={t("user.LastName")}
                                    size="small"
                                    autoComplete="new-password"
                                    onChange={(event) =>
                                        setUserData(() => ({
                                            ...user,
                                            lastName: event.target.value,
                                        }))
                                    }
                                    required
                                    value={user.lastName}
                                    inputProps={{
                                        maxLength: 50,
                                    }}
                                ></InputFilterFieldStyled>
                            </FormControl>
                            <FormControl variant="standard">
                                <InputFilterFieldStyled
                                    label={t("user.FirstName")}
                                    size="small"
                                    autoComplete="new-password"
                                    onChange={(event) =>
                                        setUserData(() => ({
                                            ...user,
                                            firstName: event.target.value,
                                        }))
                                    }
                                    required
                                    value={user.firstName}
                                    inputProps={{
                                        maxLength: 50,
                                    }}
                                ></InputFilterFieldStyled>
                            </FormControl>
                            <FormControl variant="standard">
                                <InputFilterFieldStyled
                                    id={handleGenerateId()}
                                    label={t("user.Username")}
                                    size="small"
                                    autoComplete="new-password"
                                    onChange={(event) =>
                                        setUserData(() => ({
                                            ...user,
                                            username: event.target.value,
                                        }))
                                    }
                                    required
                                    value={user.username}
                                    inputProps={{
                                        maxLength: 255,
                                    }}
                                ></InputFilterFieldStyled>
                            </FormControl>
                            <LoginInputFormStyled className="input-form">
                                <InputLabel required htmlFor="outlined-adornment-password">
                                    {t("login.Password")}
                                </InputLabel>
                                <OutlinedInput
                                    required
                                    id={`outlined-adornment-password ${handleGenerateId()}`}
                                    type={showPassword ? "text" : "password"}
                                    value={user.userPassword}
                                    size="small"
                                    autoComplete="new-password"
                                    onChange={(event) =>
                                        setUserData(() => ({
                                            ...user,
                                            userPassword: event.target.value,
                                        }))
                                    }
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label={t("login.Password")}
                                    inputProps={{
                                        maxLength: 50,
                                    }}
                                />
                                <PasswordStrengthMeter password={user.userPassword ?? ""} />
                            </LoginInputFormStyled>
                        </CreateEntityBox>
                    </Grid>
                    <Grid md={4} item className="create-user-column">
                        <Toolbar>
                            <Box
                                component="img"
                                sx={{
                                    height: "26px",
                                    width: "18px",
                                }}
                                alt={t("user.WorkPlace")}
                                src={locationIcon}
                            />
                            <Box className="create-user-title">{t("user.WorkPlace")}</Box>
                        </Toolbar>
                        <CreateEntityBox>
                            <FormControl size="small">
                                <UserProfileSelect
                                    value={user.profileId}
                                    updateProfile={(args: number) => {
                                        setUserData(() => ({
                                            ...user,
                                            profileId: args,
                                        }));
                                    }}
                                    required
                                ></UserProfileSelect>
                            </FormControl>
                            <div className="user-switch-block">
                                <FormControl className="user-switch-form-control" variant="standard" size="small">
                                    <FormControlLabel
                                        sx={{ margin: "0" }}
                                        control={
                                            <SwitchComponent
                                                sx={{ m: 1 }}
                                                checked={user.isActive}
                                                onChange={() =>
                                                    setUserData(() => ({
                                                        ...user,
                                                        isActive: !user.isActive,
                                                    }))
                                                }
                                            />
                                        }
                                        label={t("user.Status")}
                                        labelPlacement="top"
                                    />
                                </FormControl>
                            </div>
                        </CreateEntityBox>
                    </Grid>
                    <Grid md={4} item className="create-user-column">
                        <Toolbar>
                            <Box
                                component="img"
                                sx={{
                                    height: "26px",
                                    width: "22.52px",
                                }}
                                alt={t("general.ContactInfo")}
                                src={contactInfoIcon}
                            />
                            <Box className="create-user-title">{t("general.ContactInfo")}</Box>
                        </Toolbar>
                        <CreateEntityBox>
                            <FormControl variant="standard">
                                <InputFilterFieldStyled
                                    label={t("user.cellPhone")}
                                    size="small"
                                    autoComplete="new-password"
                                    onChange={(event) => handleMobilePhoneChange(event)}
                                    value={user.cellPhoneNumber}
                                    required
                                ></InputFilterFieldStyled>
                            </FormControl>
                            <FormControl variant="standard">
                                <InputFilterFieldStyled
                                    label={t("user.workPhone")}
                                    size="small"
                                    autoComplete="new-password"
                                    onChange={(event) => handleWorkPhoneChange(event)}
                                    value={user.phoneNumber}
                                    required
                                ></InputFilterFieldStyled>
                            </FormControl>
                            <FormControl variant="standard">
                                <InputFilterFieldStyled
                                    label={t("user.Email")}
                                    size="small"
                                    onChange={(event) =>
                                        setUserData(() => ({
                                            ...user,
                                            email: event.target.value,
                                        }))
                                    }
                                    autoComplete="new-password"
                                    value={user.email}
                                    inputProps={{
                                        inputMode: "email",
                                        maxLength: 255,
                                    }}
                                    required
                                ></InputFilterFieldStyled>
                            </FormControl>
                        </CreateEntityBox>
                    </Grid>
                    <Grid
                        md={12}
                        item
                        sx={{
                            justifyContent: "flex-end",
                            display: "flex",
                        }}
                    >
                        <Grid
                            item
                            sx={{
                                padding: "0 14px",
                            }}
                        >
                            <BlueButtonOutlined
                                variant="outlined"
                                size="medium"
                                sx={{ width: "175px", height: "39px", margin: "0 10px" }}
                                onClick={handleCancel}
                            >
                                {t("button.cancel")}
                            </BlueButtonOutlined>
                            <GreenButtonContained
                                variant="contained"
                                size="medium"
                                type="submit"
                                sx={{ width: "175px", height: "39px", margin: "0 10px" }}
                            >
                                {userId !== "0" ? <span>{t("button.save")}</span> : <span>{t("button.create")}</span>}
                            </GreenButtonContained>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </div>
    );
};

export default SettingsUserDetailsPage;
